import React, { FC, Fragment, useContext, useState } from 'react';
import { ILPacProposal, Proposal } from 'ts/shared/interfaces/CreateProposalParams';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { formatPriceProposal, dateFormat, formatOccupancySrt } from 'utils/shared/utils';
import { addDays } from 'date-fns';
import { formatPriceCurrency, getLogoUrl } from 'utils/utils';
import { getCurrency } from 'utils/utils';
import FreeTextModal from '../Single/FreeTextModal';
import withErrorBoundary from '../../withErrorBoundary';
import SingleProposalStyles from '../Single/SingleProposalStyles';
import { MainContext } from 'contexts/MainContext';
import { useProposalUi } from '../Assistant/proposalActions';

interface ILPacProposalProps {
  pac: ILPacProposal;
  proposal: Proposal;
  setProposal: (proposal: Proposal) => void;
  proposalIndex: number;
  openPopup: (product: string, index: number) => void;
  additionalPaymentsStr: string[];
  assistantOwner?: boolean;
}
const ILPackageProposal: FC<ILPacProposalProps> = ({ pac, assistantOwner = false, ...props }) => {
  const { t, i18n } = useTranslation();
  const [showFreeModal, setShowFreeModal] = useState(false);
  const { cdnUrl } = useContext(MainContext);

  useProposalUi(
    (intent) =>
      !!assistantOwner &&
      intent.kind === 'open-edit' &&
      intent.product === 'ilPackages' &&
      !intent.dynamic &&
      intent.index === props.proposalIndex,
    (intent) => {
      if (intent.kind === 'open-edit') {
        setShowFreeModal(true);
      }
    },
  );

  const onSaveFreeText = (desc: string, displayOrder?: number) => {
    props.setProposal({
      ...props.proposal,
      ilPackages: props.proposal.ilPackages.map((f, i) =>
        i === props.proposalIndex ? { ...f, freeText: desc, displayOrder } : f
      ),
    });
  };

  const flight = pac.item.options?.[0]?.flights?.[0] || pac.item.flights?.[0];

  const renderOptionsBound = (bound: any) => {
    return (
      <tr>
        <td className="position-relative" width={100}>
          <img
            className="pr-flt-logo"
            src={getLogoUrl(cdnUrl + '/backend/s3/prod.beprotravel/cdn/', bound.airline)}
            alt={bound.airline}
          />
        </td>
        <td>
          {bound.airline} {bound.flightNumber}
        </td>
        <td>{dateFormat(bound.date + 'T' + bound.depTime, 'dd/MM HH:mm')}</td>
        <td>
          <span className="text-bold">{bound.departureIATA} </span>
        </td>
        <td>{dateFormat(bound.date + 'T' + bound.arrTime, 'dd/MM HH:mm')}</td>
        <td>
          <span className="text-bold">{bound.arrivalIATA} </span>
        </td>
        <td></td>
        <td>{bound.flightDuration}</td>
      </tr>
    );
  };

  return (
    <div className="pr-hotel-card-wr">
      <SingleProposalStyles />
      <div className="pr-hotel-head text-bold flex-between">
        <span>{pac.item.info.hotelName || pac.item.deal.rowName}</span>
        <div>
          {!!pac.viewValue && (
            <span className="text-contrast">
              {formatPriceCurrency(pac.price, getCurrency(pac.currency))} /{' '}
            </span>
          )}
          <span
            className="text-bold text-lg text-contrast text-nowrap cur-poi"
            onClick={() => props.openPopup('ilPackages', props.proposalIndex)}
          >
            {formatPriceProposal(pac.price, pac.currency, pac.viewType, pac.viewValue)}
            <FontAwesomeIcon icon={faPencil} className="cur-poi ml-5" size="xs" />
          </span>
          <FontAwesomeIcon
            icon={faPenToSquare}
            size="lg"
            className="ml-10 cur-poi"
            onClick={() => setShowFreeModal(true)}
          />
          <FontAwesomeIcon
            icon={faTrash}
            size="lg"
            className="ml-10 cur-poi"
            onClick={() => {
              props.setProposal({
                ...props.proposal,
                ilPackages: props.proposal.ilPackages.filter((f, i) => i !== props.proposalIndex),
              });
            }}
          />
        </div>
      </div>

      <div className="pr-hotel-content">
        {!!pac.item.info.hotelImages?.length && (
          <div className="pr-hotel-image-container">
            <img className="pr-hotel-image" src={pac.item.info.hotelImages?.[0].url} alt="1" />
          </div>
        )}

        <div className="pr-hotel-details">
          <div className="pr-hotel-flex-row">
            <span className="pr-hotel-label">{t('search.destination')}:</span>
            <span className="pr-hotel-value">
              {pac.item.additionalInfo.cityFullDescription || pac.item.deal.rowName}
            </span>
          </div>
          {!!pac.item.deal.hotelRating && (
            <div className="pr-hotel-detail-row">
              <span className="pr-hotel-label">{t('hotel.stars')}:</span>
              <span className="pr-hotel-value">{pac.item.deal.hotelRating}</span>
            </div>
          )}

          {!!pac.item.info.hotelLocation?.address?.street && (
            <div className="pr-hotel-detail-row">
              <span className="pr-hotel-label">{t('hotel.address')}:</span>
              <span className="pr-hotel-value">{pac.item.info.hotelLocation.address.street}</span>
            </div>
          )}

          <div className="pr-hotel-flex-row">
            <span className="pr-hotel-label">{t('search.check_in')}:</span>
            <span className="pr-hotel-value">
              {dateFormat(pac.item.deal.departureDate, 'dd MMM yyyy', 'en')}

              <span> ({pac.item.deal.dealLength})</span>
            </span>
          </div>
          <div className="pr-hotel-flex-row">
            <span className="pr-hotel-label">{t('search.check_out')}:</span>
            <span className="pr-hotel-value">
              {dateFormat(
                addDays(new Date(pac.item.deal.departureDate), +pac.item.deal.dealLength),
                'dd MMM yyyy',
                'en'
              )}
            </span>
          </div>

          <div className="pr-hotel-flex-row">
            <span className="pr-hotel-label">{t('hotel.room')}:</span>
            <span className="pr-hotel-value">
              {pac.item.deal.roomType ||
                pac.selectedRooms
                  .flatMap((item) => {
                    const [num, count] = item.split('-').map(Number);
                    return Array(count).fill(num);
                  })
                  .map((roomId, index) => (
                    <div key={index}>
                      {
                        pac.item.options[0].prices.find((price: any) => price.bookCode === roomId)
                          ?.roomType
                      }
                    </div>
                  ))}
            </span>
          </div>
        </div>
      </div>

      <div className="pr-mt-20"></div>
      <div className="pr-hotel-title"> {t('flight.flight')}</div>
      <div className="pr-lh">
        <div className="pr-status mb-5">{t('flight.round_trip')}</div>{' '}
        <span className="ph-10 text-bold">{pac.item.deal.provider}</span>
        <div>
          {dateFormat(flight.outbound.date + 'T' + flight.outbound.depTime, 'EEE, dd/MM HH:mm')}
          <span className="ph-5">-</span>
          {dateFormat(flight.inbound.date + 'T' + flight.inbound.arrTime, 'EEE, dd/MM HH:mm')}
        </div>
        <div>
          {formatOccupancySrt(
            pac.item.options[0].prices.find((price: any) =>
              pac.selectedRooms[0].includes(price.bookCode + '')
            )?.complect || ''
          )}
        </div>
        <div className="text-bold">
          {pac.item.deal.dealBaggage?.unitHandbag === 0 ? (
            <span>{t('flight.handbag_excluded')}</span>
          ) : (
            <span>
              {t('flight.handbag_included')} {pac.item.deal.dealBaggage?.maxWeightHandbag}
              {t('kg')}
            </span>
          )}
          {pac.item.deal.dealBaggage?.unitBaggage === 0 ? (
            <span>, {t('flight.bag_excluded')}</span>
          ) : (
            <span>
              , {t('flight.bag_included')}
              {!!pac.item.deal.dealBaggage?.maxWeightBaggage && (
                <span>
                  {pac.item.deal.dealBaggage?.maxWeightBaggage} {t('kg')}{' '}
                </span>
              )}
            </span>
          )}

          {!!pac.item.deal.dealBaggage?.unitTrolley && (
            <span className="mr-5">
              , {t('flight.trolley_included')}
              {pac.item.deal.dealBaggage?.maxWeightTrolley > 0 && (
                <span className="ml-5">
                  {pac.item.deal.dealBaggage?.maxWeightTrolley} {t('kg')}
                </span>
              )}
            </span>
          )}
        </div>
        <div>
          {pac.rooms?.map((room, roomIndex) => (
            <div key={roomIndex}>
              {room.catalogAdditionalPayments.length > 0 && (
                <Fragment>
                  <div> {t('hotel.room') + ' ' + (roomIndex + 1)} </div>
                  {room.catalogAdditionalPayments.map((payment: any, paymentIndex: number) => (
                    <div key={paymentIndex}>
                      <span className="text-bold">
                        {t('passenger.guest')} {payment.paxCode}:
                        {i18n.language === 'he' ? payment.descLocal : payment.value}{' '}
                        {formatPriceCurrency(payment.addPrice, getCurrency(room.currency))}
                      </span>
                    </div>
                  ))}
                </Fragment>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pr-table-wr">
        <table className="pr-table">
          <thead>
            <tr>
              <th>{t('filter.airline')}</th>
              <th>{t('flight.flight')}</th>
              <th>{t('flight.dep_time')}</th>
              <th>{t('flight.dep_airport')}</th>
              <th>{t('flight.arr_time')}</th>
              <th>{t('flight.arr_airport')}</th>
              <th>{t('flight.aircraft')}</th>
              <th>{t('filter.duration')}</th>
            </tr>
          </thead>
          <tbody>
            {!!flight && (
              <Fragment>
                {renderOptionsBound(flight.outbound)}
                {renderOptionsBound(flight.inbound)}
              </Fragment>
            )}
          </tbody>
        </table>
      </div>

      {showFreeModal && (
        <FreeTextModal
          close={() => setShowFreeModal(false)}
          onSave={onSaveFreeText}
          freeText={pac.freeText || ''}
          displayOrder={pac.displayOrder}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(ILPackageProposal, 'ILPackageProposal');
